'use client';

import useDelayedLoader from '@/hooks/use-delayed-loader';
import { useInfiniteQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { useHotkeys } from 'react-hotkeys-hook';

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Link from 'next/link';

import ApiError from '@/components/api-error';

import { FormCard } from '@/features/home-page/components';

import { HomeCardsSkeleton } from '@/shared/page-loading-skeletons/home-skeleton';
import { getUserForms } from '@/shared/services/forms';
import { IGetUserFormsPayload } from '@/shared/services/forms/forms.types';
import { KeyboardKeys } from '@/shared/utils/navigation';

import { useRouter } from '@shared/hooks/use-router';

import { getFormDetailsPath, getFormEditPath } from '@/utils/common';
import {
	GET_USER_FORMS_QUERY_KEY,
	HOME_PAGE_TABS_VALUES_MAP,
	RECENCY,
	USER_FORMS_FETCH_COUNT,
} from '@/utils/constants';
import { getDayMonthFromUTC } from '@/utils/date';

import { useFormDashboardStore } from '../common/form-dashboard-provider';

function FormCards({ tabParamValue }: { tabParamValue: string }) {
	const router = useRouter();
	const loadMoreRef = useRef<HTMLDivElement>(null);
	const cardContainerRef = useRef<HTMLDivElement>(null);
	const [focusedFormIndex, setFocusedFormIndex] = useState<number>(-1);
	const filters = useFormDashboardStore((state) => state.filters);
	const setForms = useFormDashboardStore((state) => state.setForms);
	const setIsLoadingForms = useFormDashboardStore((state) => state.setIsLoadingForms);

	// React Query's `useInfiniteQuery` to handle infinite scrolling
	const { data, isLoading, isError, hasNextPage, fetchNextPage, isRefetching } = useInfiniteQuery({
		queryKey: [GET_USER_FORMS_QUERY_KEY, tabParamValue, filters],
		queryFn: ({ pageParam = 0 }) => {
			const payload: IGetUserFormsPayload = {
				live_only: filters.liveForms,
				closed_only: filters.closedForms,
				page_details: {
					page_size: USER_FORMS_FETCH_COUNT,
					offset: pageParam,
					sorting_type: HOME_PAGE_TABS_VALUES_MAP[RECENCY],
					sorting_order: 'DESC',
				},
				search: filters?.search || '',
				created_by_me: filters?.createdByMe || false,
			};

			return getUserForms(payload);
		},
		initialPageParam: 0,
		getNextPageParam: (response) => {
			const { pageDetails } = response;
			return pageDetails.hasNext ? pageDetails.offset + pageDetails.pageSize : undefined;
		},
	});

	const displayLoadMore = hasNextPage;
	const flatData = useMemo(() => {
		return data?.pages.flatMap((page) => page.forms) ?? [];
	}, [data]);
	const isDataAvailable = !!flatData.length;
	const showRefetchLoader = useDelayedLoader(isRefetching);
	const isFormCardsLoading = isLoading || showRefetchLoader;

	const handleLinkHover = useCallback(
		(link: string) => {
			router.prefetch(link);
		},
		[router]
	);

	useEffect(() => {
		setForms(flatData);
		const netLoading = isLoading || isRefetching;
		setIsLoadingForms(netLoading);

		if (!flatData.length) return;

		// prefetch first 5 forms
		flatData?.slice(0, 5)?.forEach((userForm) => {
			const linkToOpen =
				userForm.status === 'DRAFT'
					? getFormEditPath(userForm.identifier)
					: getFormDetailsPath(userForm.identifier);

			router.prefetch(linkToOpen);
		});
	}, [flatData, router]);

	useEffect(() => {
		const loadMoreEle = loadMoreRef.current;

		if (!loadMoreEle) return;

		const obsOptions = {
			root: null,
			threshold: 0.5,
		};

		const obsCallback = (entries: IntersectionObserverEntry[]) => {
			const [entry] = entries;

			if (entry.isIntersecting) {
				fetchNextPage();
			}
		};

		const observer = new IntersectionObserver(obsCallback, obsOptions);

		observer.observe(loadMoreEle);

		return () => {
			observer.disconnect();
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	const handleFormNavigation = useCallback(
		(isDown: boolean = false) => {
			const newFocusedFormIndex = focusedFormIndex + (isDown ? 1 : -1);
			if (newFocusedFormIndex < 0 || newFocusedFormIndex >= flatData.length) return;

			setFocusedFormIndex(newFocusedFormIndex);

			// focus on the link
			const linkElem = document.querySelector(
				`[data-form-id="${flatData[newFocusedFormIndex].identifier}"]`
			);
			if (linkElem) {
				(linkElem as HTMLLinkElement).focus();
			}
		},
		[focusedFormIndex, flatData]
	);

	// hot keys
	useHotkeys(`${KeyboardKeys.ArrowDown}`, () => handleFormNavigation(true), {
		preventDefault: true,
	});

	useHotkeys(`${KeyboardKeys.ArrowUp}`, () => handleFormNavigation(false), {
		preventDefault: true,
	});

	useHotkeys(
		`${KeyboardKeys.Escape}`,
		() => {
			cardContainerRef.current?.focus({ preventScroll: true });

			setFocusedFormIndex(-1);
		},
		{
			preventDefault: true,
		}
	);

	if (isFormCardsLoading) {
		return <HomeCardsSkeleton />;
	}

	if (isError) {
		return (
			<div className="h-[100%]">
				<ApiError />
			</div>
		);
	}

	if (!isDataAvailable) {
		const hasActiveSearch = !!filters.search;

		return (
			<div className="flex h-[100%] flex-col items-center justify-center gap-6 text-center text-primary">
				<svg
					width="64px"
					height="64px"
					viewBox="0 0 24 24"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M16 4C16.93 4 17.395 4 17.7765 4.10222C18.8117 4.37962 19.6204 5.18827 19.8978 6.22354C20 6.60504 20 7.07003 20 8V17.2C20 18.8802 20 19.7202 19.673 20.362C19.3854 20.9265 18.9265 21.3854 18.362 21.673C17.7202 22 16.8802 22 15.2 22H8.8C7.11984 22 6.27976 22 5.63803 21.673C5.07354 21.3854 4.6146 20.9265 4.32698 20.362C4 19.7202 4 18.8802 4 17.2V8C4 7.07003 4 6.60504 4.10222 6.22354C4.37962 5.18827 5.18827 4.37962 6.22354 4.10222C6.60504 4 7.07003 4 8 4M12 17V11M9 14H15M9.6 6H14.4C14.9601 6 15.2401 6 15.454 5.89101C15.6422 5.79513 15.7951 5.64215 15.891 5.45399C16 5.24008 16 4.96005 16 4.4V3.6C16 3.03995 16 2.75992 15.891 2.54601C15.7951 2.35785 15.6422 2.20487 15.454 2.10899C15.2401 2 14.9601 2 14.4 2H9.6C9.03995 2 8.75992 2 8.54601 2.10899C8.35785 2.20487 8.20487 2.35785 8.10899 2.54601C8 2.75992 8 3.03995 8 3.6V4.4C8 4.96005 8 5.24008 8.10899 5.45399C8.20487 5.64215 8.35785 5.79513 8.54601 5.89101C8.75992 6 9.03995 6 9.6 6Z"
						stroke="currentColor"
						strokeWidth="1.25"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
				<span className="inline-block text-xl font-semibold">
					{hasActiveSearch
						? 'No forms found matching your search.'
						: 'Forms you create will appear here.'}
				</span>
			</div>
		);
	}

	return (
		<>
			<div
				ref={cardContainerRef}
				className="flex flex-col focus:outline-none"
				tabIndex={-1}
			>
				<AnimatePresence mode="popLayout">
					{flatData.map((userForm, index) => {
						const formattedLastActivityDate = getDayMonthFromUTC(
							userForm.lastUpdatedTimestamp ?? ''
						);

						const linkToOpen =
							userForm.status === 'DRAFT'
								? getFormEditPath(userForm.identifier)
								: getFormDetailsPath(userForm.identifier);

						const isLastCard = index === flatData.length - 1;

						return (
							<motion.div
								key={userForm.identifier}
								layout
								initial={{ opacity: 1, x: 0 }}
								exit={{ opacity: 0, x: -50, height: 0 }}
								transition={{
									duration: 0.25,
									ease: 'easeOut',
									exit: { delay: 0.15, duration: 0.25 },
								}}
							>
								<Link
									href={linkToOpen}
									data-form-id={userForm.identifier}
									className="focus:outline-none"
									onMouseEnter={() => handleLinkHover(linkToOpen)}
								>
									<FormCard
										isFocused={focusedFormIndex === index}
										lastActivity={formattedLastActivityDate ?? ''}
										title={userForm.name}
										formStatus={userForm.status}
										formCounts={userForm.formCounts}
										isLastCard={isLastCard}
										formId={userForm.identifier}
									/>
								</Link>
							</motion.div>
						);
					})}
				</AnimatePresence>
			</div>
			{displayLoadMore && (
				<div
					ref={loadMoreRef}
					className="text-center"
				>
					{'Loading more...'}
				</div>
			)}
		</>
	);
}

export default memo(FormCards);
